import React, { useEffect, useState } from 'react';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import { Mountain, ArrowLeft, Lock, Mail, Loader2 } from 'lucide-react';

interface VerifyIdentityPageProps {
  onVerified: () => void;
  onBack: () => void;
}

export const VerifyIdentityPage: React.FC<VerifyIdentityPageProps> = ({ onVerified, onBack }) => {
  const { user, sendEmailLink, isRecentlyAuthenticated, markRecentlyAuthenticated } = useAuth();
  const [error, setError] = useState('');
  const [needsReauth, setNeedsReauth] = useState<boolean | null>(null); // null = checking
  const [reauthEmailSent, setReauthEmailSent] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [verifyingPassword, setVerifyingPassword] = useState(false);

  useEffect(() => {
    if (isRecentlyAuthenticated) {
      onVerified();
    } else {
      setNeedsReauth(true);
    }
  }, [isRecentlyAuthenticated, onVerified]);

  const handleSendReauthEmail = async () => {
    if (!user?.email) return;
    setSendingEmail(true);
    setError('');
    try {
      await sendEmailLink(user.email);
      setReauthEmailSent(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send verification email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handlePasswordReauth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.email || !currentPassword) return;
    setVerifyingPassword(true);
    setError('');
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      markRecentlyAuthenticated();
      onVerified();
    } catch (err: any) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Incorrect password. Please try again.');
      } else {
        setError(err.message || 'Failed to verify password');
      }
    } finally {
      setVerifyingPassword(false);
    }
  };

  // Loading state while checking recency
  if (needsReauth === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4 py-8">
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <div className="bg-gradient-to-br from-green-500 to-blue-600 p-3 rounded-xl">
              <Mountain className="h-10 w-10 text-white" />
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Verify your identity
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            For security, you need to sign in again before performing sensitive operations.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 space-y-4">
          <button
            onClick={onBack}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Dashboard
          </button>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          {reauthEmailSent ? (
            <div className="text-center space-y-3 py-4">
              <div className="flex justify-center">
                <div className="bg-blue-100 p-3 rounded-full">
                  <Mail className="h-8 w-8 text-blue-600" />
                </div>
              </div>
              <p className="text-sm font-medium text-gray-900">Check your email</p>
              <p className="text-sm text-gray-600">
                We sent a sign-in link to <strong>{user?.email}</strong>. Click the link to verify your identity.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                We'll send a sign-in link to <strong>{user?.email}</strong> to verify it's you.
              </p>
              <button
                onClick={handleSendReauthEmail}
                disabled={sendingEmail}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Mail className="h-4 w-4" />
                {sendingEmail ? 'Sending...' : 'Send verification email'}
              </button>

              <div className="relative py-3">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-white px-2 text-gray-500">or</span>
                </div>
              </div>

              <p className="text-sm text-gray-600">
                If you know your current password, you can use it to verify your identity.
              </p>

              <form onSubmit={handlePasswordReauth} className="space-y-3">
                <div>
                  <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Current Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="currentPassword"
                      name="currentPassword"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Enter current password"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={verifyingPassword || !currentPassword}
                  className="w-full flex items-center justify-center gap-2 py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Lock className="h-4 w-4" />
                  {verifyingPassword ? 'Verifying...' : 'Verify with password'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
